/**
 * aiApi.js
 * -----------------------------------------------------------------------------
 * Thin client for the /api/ai backend. The frontend NEVER talks to an AI
 * provider directly – everything goes through our own server. This module keeps
 * all AI network calls in one place so the UI component stays clean.
 */

import axios from 'axios';
import config from '../../config';

const BASE = `${config.apiUrl}/api/ai`;

/**
 * Build auth headers. We support both the JWT (if the app stored one) and the
 * userId fallback used throughout this codebase.
 */
const authHeaders = (userId) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('chatapp_token');
  if (token) headers.Authorization = `Bearer ${token}`;
  if (userId) headers['x-user-id'] = userId;
  return headers;
};

export async function getConfig(userId) {
  const res = await axios.get(`${BASE}/config`, {
    params: { userId },
    headers: authHeaders(userId),
  });
  return res.data;
}

export async function listConversations(userId) {
  const res = await axios.get(`${BASE}/conversations`, {
    params: { userId },
    headers: authHeaders(userId),
  });
  return res.data.conversations || [];
}

export async function getHistory(userId, conversationId) {
  const res = await axios.get(`${BASE}/history`, {
    params: { userId, conversationId },
    headers: authHeaders(userId),
  });
  return res.data.messages || [];
}

export async function createConversation(userId) {
  const res = await axios.post(`${BASE}/new`, { userId }, { headers: authHeaders(userId) });
  return res.data.conversation;
}

export async function clearMemory(userId, conversationId) {
  const res = await axios.post(
    `${BASE}/clear`,
    { userId, conversationId },
    { headers: authHeaders(userId) }
  );
  return res.data;
}

export async function deleteMessage(userId, messageId) {
  const res = await axios.delete(`${BASE}/message/${messageId}`, {
    data: { userId },
    headers: authHeaders(userId),
  });
  return res.data;
}

export async function starMessage(userId, messageId) {
  const res = await axios.post(
    `${BASE}/message/${messageId}/star`,
    { userId },
    { headers: authHeaders(userId) }
  );
  return res.data;
}

export async function renameConversation(userId, conversationId, title) {
  const res = await axios.post(
    `${BASE}/conversations/${conversationId}/rename`,
    { userId, title },
    { headers: authHeaders(userId) }
  );
  return res.data.conversation;
}

/**
 * Build the export download URL (streamed as a file by the server).
 */
export function exportUrl(userId, conversationId, format = 'md') {
  const params = new URLSearchParams({ userId, conversationId, format });
  return `${BASE}/export?${params.toString()}`;
}

/**
 * Stream a chat completion using fetch + ReadableStream so we can render tokens
 * gradually (ChatGPT style). Parses our custom SSE events: token / done / error.
 *
 * @param {Object} params
 * @param {String} params.userId
 * @param {String} [params.conversationId]
 * @param {String} params.prompt
 * @param {Object} [params.settings]
 * @param {Array}  [params.attachments]
 * @param {AbortSignal} [params.signal]
 * @param {(text:string)=>void} params.onToken
 * @param {(data:object)=>void} params.onDone
 * @param {(err:object)=>void}  params.onError
 */
export async function streamChat({
  userId,
  conversationId,
  prompt,
  settings = {},
  attachments = [],
  signal,
  onToken,
  onDone,
  onError,
}) {
  try {
    const response = await fetch(`${BASE}/chat`, {
      method: 'POST',
      headers: authHeaders(userId),
      body: JSON.stringify({
        userId,
        conversationId,
        prompt,
        settings,
        attachments,
        stream: true,
      }),
      signal,
    });

    if (!response.ok || !response.body) {
      let errMsg = `Request failed (${response.status})`;
      try {
        const data = await response.json();
        errMsg = data.error || errMsg;
      } catch {
        /* ignore */
      }
      onError?.({ error: errMsg });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Split on SSE frame boundaries (blank line).
      const frames = buffer.split('\n\n');
      buffer = frames.pop() || '';

      for (const frame of frames) {
        const lines = frame.split('\n');
        let event = 'message';
        let dataStr = '';
        for (const line of lines) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
        }
        if (!dataStr) continue;
        let data;
        try {
          data = JSON.parse(dataStr);
        } catch {
          continue;
        }
        if (event === 'token') onToken?.(data.text || '');
        else if (event === 'done') onDone?.(data);
        else if (event === 'error') onError?.(data);
      }
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      onError?.({ error: 'aborted', aborted: true });
    } else {
      onError?.({ error: err.message || 'Network error' });
    }
  }
}
